import currency from "currency.js";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  GetAccount200ResponsePositionsInner,
  MarginTypeResponse,
  SideResponse,
} from "../../../../codegen-api";
import { COLORS } from "../../../../constants/design/colors";
import { SPACING } from "../../../../constants/design/spacing";
import { MarketInstrumentContext } from "../../../../contexts/MarketInstrumentContext";
import { useGetAccount } from "../../../../hooks/api/account/useGetAccount";
import {
  formatCurrencyWithSubscript,
  formatSizePrecision,
} from "../../../../utils/format";
import { getMarginTypeLabel } from "../../../../utils/margin";
import { apiNum } from "../../../../utils/math";
import {
  formatKnownValue,
  formatRoiPercent,
} from "../../../../utils/unknownNumbers";
import { getProfitTextColor } from "../../../../utils/strings";
import ShareButton from "../../../Buttons/ShareButton";
import { Button, ButtonThemeEnum } from "../../../Buttons/styles";
import { ViewDetailsContent, ViewDetailsRow } from "../style";
import { UnrealizedPnlCard } from "./style";
import { PosInfo } from "./types";

interface IPositionDetailsContentProps {
  position: GetAccount200ResponsePositionsInner;
  posInfo: PosInfo | undefined;
  onOpenPositionConfirmationModal: () => void;
  onOpenPnLCard: () => void;
}

function PositionDetailsContent({
  position,
  posInfo,
  onOpenPositionConfirmationModal,
  onOpenPnLCard,
}: IPositionDetailsContentProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "shared.ViewDetailsModal.PositionDetailsModal",
  });
  const { data: accountData } = useGetAccount();
  const { getMarketPrecision } = useContext(MarketInstrumentContext);

  const accountLeverage = useMemo(
    () =>
      accountData?.leverages?.find(
        (lev) => lev.instrument_id === position?.instrument_id
      )?.leverage,
    [accountData?.leverages, position?.instrument_id]
  );
  const isCrossEstimate =
    position?.liquidation_price_type === "cross_estimate" ||
    position?.margin_type === MarginTypeResponse.Cross ||
    String(position?.margin_type || "").toLowerCase() === "cross";
  const liquidationPriceValue = Number(position?.liquidation_price);
  const hasLiquidationPrice =
    position?.liquidation_price &&
    !Number.isNaN(liquidationPriceValue) &&
    liquidationPriceValue > 0;

  return (
    <ViewDetailsContent>
      <UnrealizedPnlCard pnl={posInfo?.unrealizedPnl || 0}>
        <span>{t("unrealized_pnl")}</span>
        <span>
          {formatCurrencyWithSubscript(posInfo?.unrealizedPnl, {
            showPositiveSign: false,
          })}
        </span>
        <span>ROI</span>
        <span>{formatRoiPercent(posInfo?.profitPercent)}</span>
      </UnrealizedPnlCard>
      <ViewDetailsRow>
        <span>{t("side")}</span>
        <span
          style={{
            color:
              position.side === SideResponse.Buy
                ? COLORS.up.one
                : COLORS.down.one,
          }}
        >
          {position.side === SideResponse.Buy ? t("long") : t("short")}
        </span>
      </ViewDetailsRow>
      {!position.option ? (
        <ViewDetailsRow>
          <span>{t("margin_type", { defaultValue: "Margin Type" })}</span>
          <span>{getMarginTypeLabel(position.margin_type)}</span>
        </ViewDetailsRow>
      ) : null}
      <ViewDetailsRow>
        <span>{t("contracts")}</span>
        <span>{formatSizePrecision(position.amount)}</span>
      </ViewDetailsRow>
      <ViewDetailsRow>
        <span>{t("avg_entry_price")}</span>
        <span>
          {currency(position.avg_entry_price || 0, {
            precision: getMarketPrecision(
              position.asset,
              position.instrument_type
            ).price_precision,
          }).format()}
        </span>
      </ViewDetailsRow>
      <ViewDetailsRow>
        <span>{t("total")}</span>
        <span>
          {formatKnownValue(
            posInfo?.totalCurrentValue,
            getMarketPrecision(position.asset, position.instrument_type)
              .price_precision
          )}
        </span>
      </ViewDetailsRow>
      <ViewDetailsRow>
        <span>{t("maintenance_margin")}</span>
        <span>{formatKnownValue(position.maintenance_margin)}</span>
      </ViewDetailsRow>
      {!position.option ? (
        <ViewDetailsRow
          style={{
            textTransform: "none",
          }}
        >
          <span>{t("leverage")}</span>
          <span>
            {apiNum(accountLeverage) !== undefined ? `${accountLeverage}x` : "-"}
          </span>
        </ViewDetailsRow>
      ) : null}
      {hasLiquidationPrice ? (
        <ViewDetailsRow>
          <span>
            {isCrossEstimate
              ? t("estimated_liquidation_price", {
                  defaultValue: "Est. Liquidation Price",
                })
              : t("liquidation_price")}
          </span>
          <span>
            {currency(liquidationPriceValue, {
              precision: getMarketPrecision(
                position.asset,
                position.instrument_type
              ).price_precision,
            }).format()}
          </span>
        </ViewDetailsRow>
      ) : null}
      {isCrossEstimate && position.cross_margin_health ? (
        <ViewDetailsRow>
          <span>
            {t("cross_margin_health", {
              defaultValue: "Cross Margin Health",
            })}
          </span>
          <span>{position.cross_margin_health}%</span>
        </ViewDetailsRow>
      ) : null}
      {posInfo?.pendingFunding ? (
        <ViewDetailsRow>
          <span>{t("pending_funding")}</span>
          <span
            style={{
              color: getProfitTextColor(posInfo.pendingFunding),
            }}
          >
            {formatCurrencyWithSubscript(posInfo.pendingFunding, {
              showPositiveSign: true,
            })}
          </span>
        </ViewDetailsRow>
      ) : null}
      {posInfo?.totalFunding ? (
        <ViewDetailsRow>
          <span>{t("total_funding")}</span>
          <span
            style={{
              color: getProfitTextColor(posInfo.totalFunding),
            }}
          >
            {formatCurrencyWithSubscript(posInfo.totalFunding, {
              showPositiveSign: true,
            })}
          </span>
        </ViewDetailsRow>
      ) : null}
      {position.option ? (
        <>
          <ViewDetailsRow>
            <span>{t("delta")}</span>
            <span>{Number(position.option.delta).toFixed(2)}</span>
          </ViewDetailsRow>
          <ViewDetailsRow>
            <span>{t("theta")}</span>
            <span>{currency(position.option.theta).format()}</span>
          </ViewDetailsRow>
          <ViewDetailsRow>
            <span>{t("vega")}</span>
            <span>{Number(position.option.vega).toFixed(2)}</span>
          </ViewDetailsRow>
          <ViewDetailsRow>
            <span>{t("gamma")}</span>
            <span>{Number(position.option.gamma).toFixed(6)}</span>
          </ViewDetailsRow>
        </>
      ) : null}
      {!accountData?.in_liquidation ? (
        <div style={{ display: "flex" }}>
          <Button
            fullWidth
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            onClick={onOpenPositionConfirmationModal}
            style={{
              marginRight: SPACING.two,
            }}
          >
            {t("close_position")}
          </Button>
          <ShareButton onClick={onOpenPnLCard} />
        </div>
      ) : null}
    </ViewDetailsContent>
  );
}

export default PositionDetailsContent;
